import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import 'highlight.js/styles/github-dark.css';

const MarkdownContent = ({ content, darkMode, onInternalLink }) => {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(index);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Handle clicks on internal links
  useEffect(() => {
    const handleClick = (e) => {
      const target = e.target.closest('a');
      if (!target) return;
      
      const href = target.getAttribute('href');
      // Check if it's an internal hash link (starts with #)
      if (href && href.startsWith('#') && !href.startsWith('#heading-')) {
        e.preventDefault();
        const pageId = href.substring(1); // Remove the #
        if (onInternalLink) {
          onInternalLink(pageId);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [onInternalLink]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight, rehypeRaw]}
      components={{
        // Customize heading styles
        h1: ({ node, children, ...props }) => (
          <h1 
            className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`} 
            {...props}
          >
            {children}
          </h1>
        ),
        h2: ({ node, children, ...props }) => {
          const text = children?.[0]?.toString() || '';
          const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          return (
            <h2 
              id={id}
              className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-6 mt-12`}
              {...props}
            >
              {children}
            </h2>
          );
        },
        h3: ({ node, children, ...props }) => (
          <h3 
            className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 mt-8`} 
            {...props}
          >
            {children}
          </h3>
        ),
        h4: ({ node, children, ...props }) => (
          <h4 
            className={`text-lg font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'} mb-3 mt-6`} 
            {...props}
          >
            {children}
          </h4>
        ),
        // Customize paragraphs
        p: ({ node, ...props }) => (
          <p 
            className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4 leading-relaxed`} 
            {...props} 
          />
        ),
        // Customize lists
        ul: ({ node, ...props }) => (
          <ul 
            className={`list-disc list-inside ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4 space-y-2 ml-6`} 
            {...props} 
          />
        ),
        ol: ({ node, ...props }) => (
          <ol 
            className={`list-decimal list-inside ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4 space-y-2 ml-6`} 
            {...props} 
          />
        ),
        li: ({ node, children, ...props }) => (
          <li className="ml-4" {...props}>
            {children}
          </li>
        ),
        // Customize code blocks
        code: ({ node, inline, className, children, ...props }) => {
          if (inline) {
            return (
              <code 
                className={`${darkMode ? 'bg-gray-800 text-blue-400' : 'bg-gray-100 text-blue-600'} px-1.5 py-0.5 rounded text-sm font-mono`}
                {...props}
              >
                {children}
              </code>
            );
          }
          
          // Extract language from className (format: language-javascript)
          const language = className?.replace('language-', '') || 'text';
          const codeString = String(children).replace(/\n$/, '');
          
          // Determine if this is a response block (contains json and looks like a response)
          const isResponse = language === 'json' && (
            codeString.includes('"id":') || 
            codeString.includes('"error":') ||
            codeString.includes('"data":')
          );
          
          // Generate unique index for this code block
          const codeIndex = `${language}-${codeString.substring(0, 20)}`;
          
          return (
            <div className="mb-6 not-prose">
              {/* Optional: Add a label for the code type */}
              {language && language !== 'text' && (
                <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-200'} px-4 py-2 rounded-t-lg border-b ${darkMode ? 'border-gray-600' : 'border-gray-300'} flex justify-between items-center`}>
                  <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {language === 'bash' ? 'Request' : language === 'json' && isResponse ? 'Response' : language}
                  </span>
                  <button
                    onClick={() => copyToClipboard(codeString, codeIndex)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
                      darkMode 
                        ? 'hover:bg-gray-600 text-gray-300' 
                        : 'hover:bg-gray-300 text-gray-700'
                    }`}
                    title="Copy code"
                  >
                    {copiedCode === codeIndex ? (
                      <>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>
              )}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border ${language && language !== 'text' ? 'rounded-b-lg' : 'rounded-lg'} overflow-x-auto`}>
                <pre className="p-6 m-0 overflow-x-auto">
                  <code className={className} {...props}>
                    {children}
                  </code>
                </pre>
              </div>
            </div>
          );
        },
        // Customize pre (code block wrapper)
        pre: ({ node, children, ...props }) => {
          return <>{children}</>;
        },
        // Customize links
        a: ({ node, href, children, ...props }) => {
          // Check if it's an internal link (starts with #)
          const isInternal = href && href.startsWith('#') && !href.includes('heading-');
          
          return (
            <a 
              href={href}
              className={`${
                darkMode 
                  ? 'text-blue-400 hover:text-blue-300 hover:underline' 
                  : 'text-blue-600 hover:text-blue-800 hover:underline'
              } ${isInternal ? 'cursor-pointer' : ''} transition-all duration-150`}
              {...props}
            >
              {children}
            </a>
          );
        },
        // Customize blockquotes (for callouts/notes)
        blockquote: ({ node, children, ...props }) => (
          <blockquote 
            className={`${darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'} border-l-4 border-blue-600 p-4 mb-6 rounded-r`}
            {...props}
          >
            <div className={darkMode ? 'text-blue-200' : 'text-blue-900'}>
              {children}
            </div>
          </blockquote>
        ),
        // Customize tables
        table: ({ node, ...props }) => (
          <div className="overflow-x-auto mb-6 not-prose">
            <table 
              className={`min-w-full ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'} rounded-lg`} 
              {...props} 
            />
          </div>
        ),
        thead: ({ node, ...props }) => (
          <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'} {...props} />
        ),
        th: ({ node, ...props }) => (
          <th 
            className={`px-4 py-3 ${darkMode ? 'text-white' : 'text-gray-900'} font-semibold text-left border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`} 
            {...props} 
          />
        ),
        td: ({ node, ...props }) => (
          <td 
            className={`px-4 py-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} 
            {...props} 
          />
        ),
        // Customize horizontal rules
        hr: ({ node, ...props }) => (
          <hr 
            className={`my-8 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} 
            {...props} 
          />
        ),
        // Customize strong/bold
        strong: ({ node, ...props }) => (
          <strong 
            className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} 
            {...props} 
          />
        ),
        // Customize emphasis/italic
        em: ({ node, ...props }) => (
          <em 
            className="italic" 
            {...props} 
          />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownContent;